import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import FormField from "../../../components/ui/FormField";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import { getSocieties } from "../../../services/societyService";
import { getFlatsBySociety } from "../../../services/flatsService";
import { assignParkingSlot, listParkingSlots } from "../../../services/parkingService";
import { parkingAssignSchema, zodErrorToFieldErrors } from "../../../schemas/parkingSlotSchema";
import styles from "./ParkingForm.module.css";

const AssignParkingSlot = () => {
  const navigate = useNavigate();

  const [societies, setSocieties] = useState([]);
  const [slots, setSlots] = useState([]);
  const [flats, setFlats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    society_id: "",
    slot_id: "",
    flat_id: "",
  });

  const loadSocieties = useCallback(async () => {
    const res = await getSocieties();
    setSocieties(Array.isArray(res) ? res : []);
  }, []);

  const loadSocietyData = useCallback(async () => {
    if (!form.society_id) {
      setSlots([]);
      setFlats([]);
      return;
    }

    setLoading(true);
    try {
      const [slotsRes, flatsRes] = await Promise.all([
        listParkingSlots({
          society_id: form.society_id,
          assigned: "false",
          limit: 200,
          offset: 0,
        }),
        getFlatsBySociety(form.society_id),
      ]);
      setSlots(slotsRes.slots || []);
      setFlats(Array.isArray(flatsRes) ? flatsRes : []);
    } catch (error) {
      console.error("LOAD PARKING ASSIGN DATA ERROR:", error);
      setSlots([]);
      setFlats([]);
    } finally {
      setLoading(false);
    }
  }, [form.society_id]);

  useEffect(() => {
    loadSocieties();
  }, [loadSocieties]);

  useEffect(() => {
    loadSocietyData();
  }, [loadSocietyData]);

  useEffect(() => {
    setForm((p) => ({ ...p, slot_id: "", flat_id: "" }));
    setErrors({});
  }, [form.society_id]);

  const flatLabel = useMemo(() => {
    const map = new Map();
    flats.forEach((f) => map.set(String(f.flat_id), String(f.flat_number)));
    return (id) => map.get(String(id)) || "";
  }, [flats]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsed = parkingAssignSchema.safeParse({
      slot_id: form.slot_id,
      flat_id: form.flat_id,
    });
    if (!parsed.success) {
      setErrors(zodErrorToFieldErrors(parsed.error));
      return;
    }

    setLoading(true);
    try {
      await assignParkingSlot(parsed.data.slot_id, { flat_id: parsed.data.flat_id });
      navigate("/parking-management");
    } catch (error) {
      console.error("ASSIGN PARKING SLOT ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Parking Management">
      <div className={styles.page}>
        <form className={styles.card} onSubmit={handleSubmit}>
          <h2 className={styles.title}>Assign Parking Slot</h2>

          <div className={styles.grid}>
            <FormField label="Society / Property" required>
              <SelectDropdown
                value={form.society_id}
                onChange={(e) => setForm((p) => ({ ...p, society_id: e.target.value }))}
                placeholder="Select society..."
                options={societies.map((s) => ({
                  label: s.society_name,
                  value: String(s.society_id),
                }))}
              />
            </FormField>

            <FormField label="Available Parking Slot" required error={errors.slot_id}>
              <SelectDropdown
                value={form.slot_id}
                onChange={(e) => {
                  setForm((p) => ({ ...p, slot_id: e.target.value }));
                  setErrors((p) => ({ ...p, slot_id: "" }));
                }}
                disabled={!form.society_id || loading}
                placeholder={!form.society_id ? "Select society first" : "Select slot..."}
                options={slots.map((s) => ({
                  label: `${s.slot_code} ${s.slot_type ? `(${s.slot_type})` : ""}`,
                  value: String(s.slot_id),
                }))}
              />
            </FormField>

            <FormField
              label="Unit / Flat"
              required
              error={errors.flat_id}
              hint={form.flat_id ? `Selected unit: ${flatLabel(form.flat_id)}` : undefined}
            >
              <SelectDropdown
                value={form.flat_id}
                onChange={(e) => {
                  setForm((p) => ({ ...p, flat_id: e.target.value }));
                  setErrors((p) => ({ ...p, flat_id: "" }));
                }}
                disabled={!form.society_id || loading}
                placeholder={!form.society_id ? "Select society first" : "Select unit..."}
                options={flats.map((f) => ({
                  label: `Unit ${f.flat_number}`,
                  value: String(f.flat_id),
                }))}
              />
            </FormField>
          </div>

          <div className={styles.actions}>
            <Button variant="outline" type="button" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={!form.society_id}>
              Assign
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AssignParkingSlot;
